import { useEffect, useRef, useState } from "react";
import type { AnalysisResult, ChainId, TokenCandidate } from "../server/types";
import { Landing } from "./components/Landing";
import { MapScreen } from "./components/MapScreen";
import { Scanning } from "./components/Scanning";
import type { ChainChoice } from "./components/SearchBox";

type View = "landing" | "scanning" | "map";
const appBase = import.meta.env.BASE_URL === "/" ? "" : import.meta.env.BASE_URL.replace(/\/$/, "");

function isDirectAddress(query: string, chain: ChainChoice): chain is ChainId {
  if (chain === "auto") return false;
  return chain === "solana"
    ? /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(query)
    : /^0x[a-fA-F0-9]{40}$/.test(query);
}

function localTokenIcon(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const parsed = new URL(value, window.location.origin);
    const sameOrigin = parsed.origin === window.location.origin;
    const dexBriefAsset = parsed.origin === "https://dexbrief.com";
    return (sameOrigin || dexBriefAsset) && parsed.pathname.startsWith("/assets/token-icons/")
      ? parsed.href
      : undefined;
  } catch {
    return undefined;
  }
}

function tokenLocation(token: TokenCandidate, embedded: boolean): string {
  const params = new URLSearchParams({
    chain: token.chain,
    address: token.address,
    symbol: token.symbol,
    name: token.name,
  });
  const icon = localTokenIcon(token.imageUrl);
  if (icon) params.set("icon", icon);
  if (embedded) params.set("embed", "1");
  return `?${params.toString()}`;
}

async function apiJson<T>(url: string): Promise<T> {
  const response = await fetch(`${appBase}${url}`);
  const payload = (await response.json()) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error || "Request failed");
  return payload;
}

export default function App() {
  const embedded = new URLSearchParams(window.location.search).get("embed") === "1";
  const [view, setView] = useState<View>("landing");
  const [query, setQuery] = useState("");
  const [chain, setChain] = useState<ChainChoice>("auto");
  const [results, setResults] = useState<TokenCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [activeToken, setActiveToken] = useState<TokenCandidate | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [stage, setStage] = useState(0);
  const booted = useRef(false);

  const runAnalysis = async (token: TokenCandidate, replaceHistory = false) => {
    setActiveToken(token);
    setAnalysis(null);
    setError("");
    setStage(0);
    setView("scanning");

    // Searching by raw address gives us no identity, so the scan screen would sit
    // on "Token" and "?" for the whole wait. Resolve the real name and icon
    // alongside the scan rather than blocking on it.
    if (!token.imageUrl || token.symbol === "?") {
      void apiJson<{ results: TokenCandidate[] }>(
        `/api/search?q=${encodeURIComponent(token.address)}&chain=${token.chain}`,
      )
        .then((payload) => {
          const match = payload.results.find(
            (row) => row.chain === token.chain && row.address.toLowerCase() === token.address.toLowerCase(),
          );
          if (!match) return;
          // Only apply if the user is still waiting on this same token.
          setActiveToken((current) =>
            current && current.address === token.address ? { ...current, ...match } : current,
          );
        })
        .catch(() => {
          // Identity is cosmetic here; the scan itself is the work that matters.
        });
    }

    const timer = window.setInterval(() => setStage((current) => Math.min(3, current + 1)), 1500);
    try {
      const payload = await apiJson<AnalysisResult>(
        `/api/analyze?chain=${token.chain}&address=${encodeURIComponent(token.address)}`,
      );
      const icon = localTokenIcon(token.imageUrl);
      setAnalysis({
        ...payload,
        token: {
          ...payload.token,
          name: token.name && token.name !== "Token" ? token.name : payload.token.name,
          symbol: token.symbol && token.symbol !== "?" ? token.symbol : payload.token.symbol,
          ...(icon ? { imageUrl: icon } : {}),
        },
      });
      setStage(4);
      setView("map");
      const url = tokenLocation(token, embedded);
      window.history[replaceHistory ? "replaceState" : "pushState"]({}, "", url);
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : "The scan failed.");
      setView("landing");
    } finally {
      window.clearInterval(timer);
    }
  };

  const search = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setError("");
    setResults([]);
    if (isDirectAddress(trimmed, chain)) {
      void runAnalysis({ chain, address: trimmed, name: "Token", symbol: "?" });
      return;
    }
    setSearching(true);
    try {
      const chainParam = chain === "auto" ? "" : `&chain=${chain}`;
      const payload = await apiJson<{ results: TokenCandidate[] }>(
        `/api/search?q=${encodeURIComponent(trimmed)}${chainParam}`,
      );
      if (payload.results.length === 1) void runAnalysis(payload.results[0]);
      else if (payload.results.length) setResults(payload.results);
      else setError("No matching token with a tracked market was found on SOL, BSC, or Robinhood Chain. Try the exact contract address and choose its chain.");
    } catch (searchError) {
      setError(searchError instanceof Error ? searchError.message : "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const reset = () => {
    setView("landing");
    setAnalysis(null);
    setActiveToken(null);
    setResults([]);
    setError("");
    window.history.pushState({}, "", window.location.pathname);
  };

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    const params = new URLSearchParams(window.location.search);
    const urlChain = params.get("chain") as ChainId | null;
    const address = params.get("address");
    if (urlChain && address && ["solana", "ethereum", "bsc", "robinhood"].includes(urlChain)) {
      setQuery(address);
      setChain(urlChain);
      const symbol = (params.get("symbol") || "?").slice(0, 32);
      const name = (params.get("name") || "Token").slice(0, 120);
      const imageUrl = localTokenIcon(params.get("icon") || undefined);
      void runAnalysis({ chain: urlChain, address, name, symbol, ...(imageUrl ? { imageUrl } : {}) }, true);
    }
  }, []);

  if (view === "scanning" && activeToken) return <Scanning token={activeToken} stage={stage} />;
  if (view === "map" && analysis) {
    return (
      <MapScreen
        data={analysis}
        embedded={embedded}
        onBack={reset}
        onNewSearch={reset}
        onRefresh={(token) => void runAnalysis(token, true)}
      />
    );
  }
  return (
    <Landing
      query={query}
      chain={chain}
      searching={searching}
      error={error}
      results={results}
      onQueryChange={setQuery}
      onChainChange={setChain}
      onSearch={() => void search()}
      onAnalyze={(token) => void runAnalysis(token)}
    />
  );
}
